import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { logAudit } from '../../../lib/audit';
import { ADMIN_ROLES, roleLabel, roleColor, type AdminRecord } from './types';

/* ─── Add Admin Modal ─────────────────────────────── */
const AddAdminModal = ({
  currentUserId,
  onClose,
  onAdded,
}: {
  currentUserId: string;
  onClose: () => void;
  onAdded: () => void;
}) => {
  const [step, setStep] = useState<'email' | 'confirm'>('email');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof ADMIN_ROLES)[number]>('admin');
  const [foundProfile, setFoundProfile] = useState<{ id: string; full_name: string; email: string; avatar_url: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const searchEmail = async () => {
    if (!email.trim()) { setError('Informe o e-mail.'); return; }
    setLoading(true);
    setError('');
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, name, email, avatar_url')
      .eq('email', email.trim().toLowerCase())
      .maybeSingle();
    if (!data) { setError('Nenhum usuário com este e-mail.'); setLoading(false); return; }
    setFoundProfile({ id: data.id, full_name: data.full_name || data.name || '', email: data.email, avatar_url: data.avatar_url });
    setStep('confirm');
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!foundProfile) return;
    setLoading(true);
    const { error: err } = await supabase.from('user_roles').upsert(
      { user_id: foundProfile.id, role, status: 'active', invited_by: currentUserId, invited_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
    if (err) { setError('Erro: ' + err.message); setLoading(false); return; }
    await logAudit({ action: 'create', resource: 'admin', resource_id: foundProfile.id, details: { role, email: foundProfile.email } });
    setSuccess(true);
    setTimeout(() => onAdded(), 1200);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1A2E] border border-white/10 rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[16px] font-700 text-white">Adicionar Administrador</h3>
            <p className="text-[12px] text-white/40 mt-0.5">O usuário deve ter uma conta ativa na plataforma</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white/10 text-white/50 cursor-pointer transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center py-8">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mb-3">
              <i className="ri-check-line text-emerald-400 text-[28px]" />
            </div>
            <p className="text-white font-600 text-[15px]">Administrador adicionado!</p>
            <p className="text-white/40 text-[12px] mt-1">Acesse a ficha para completar os dados.</p>
          </div>
        ) : step === 'email' ? (
          <div className="space-y-4">
            <div>
              <label className="block text-[12px] text-white/50 mb-1.5">E-mail do usuário</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchEmail()}
                placeholder="email@exemplo.com"
                autoFocus
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 placeholder:text-white/20"
              />
            </div>
            {error && <p className="text-[12px] text-red-400 flex items-center gap-1.5"><i className="ri-error-warning-line" />{error}</p>}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 border border-white/10 text-white/60 text-[13px] font-600 rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-colors">
                Cancelar
              </button>
              <button onClick={searchEmail} disabled={loading} className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                {loading ? 'Buscando...' : 'Buscar usuário'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Found user preview */}
            <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-violet-600/30 flex items-center justify-center shrink-0">
                {foundProfile?.avatar_url ? (
                  <img src={foundProfile.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <span className="text-violet-300 font-700">{(foundProfile?.full_name || 'U')[0].toUpperCase()}</span>
                )}
              </div>
              <div>
                <p className="text-[14px] font-600 text-white">{foundProfile?.full_name || 'Usuário'}</p>
                <p className="text-[12px] text-white/40">{foundProfile?.email}</p>
              </div>
              <button onClick={() => { setStep('email'); setFoundProfile(null); setError(''); }} className="ml-auto text-[11px] text-white/30 hover:text-violet-400 cursor-pointer transition-colors">
                Trocar
              </button>
            </div>
            <div>
              <label className="block text-[12px] text-white/50 mb-1.5">Papel administrativo</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as any)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500"
              >
                {ADMIN_ROLES.map((r) => (
                  <option key={r} value={r} className="bg-[#1A1A2E]">{roleLabel[r]}</option>
                ))}
              </select>
            </div>
            <div className="p-3 bg-violet-600/10 border border-violet-500/20 rounded-xl text-[11px] text-violet-300">
              <i className="ri-information-line mr-1.5" />
              Após adicionar, acesse a ficha completa para complementar dados, endereço e foto.
            </div>
            {error && <p className="text-[12px] text-red-400 flex items-center gap-1.5"><i className="ri-error-warning-line" />{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => { setStep('email'); setFoundProfile(null); }} className="flex-1 py-2.5 border border-white/10 text-white/60 text-[13px] font-600 rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-colors">
                Voltar
              </button>
              <button onClick={handleAdd} disabled={loading} className="flex-1 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                {loading ? 'Adicionando...' : 'Confirmar e adicionar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Page ─────────────────────────────────────── */
const AdminAdministradoresPage = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [currentUserId, setCurrentUserId] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => { if (data?.user) setCurrentUserId(data.user.id); });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: roles } = await supabase
      .from('user_roles')
      .select('id, user_id, role, status, invited_by, invited_at, created_at')
      .in('role', [...ADMIN_ROLES])
      .order('created_at', { ascending: false });

    const userIds = (roles || []).map((r: any) => r.user_id);
    let profileMap: Record<string, any> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, display_name, name, email, phone, cpf, avatar_url, cep, street, street_number, complement, neighborhood, city, state, country, last_seen_at')
        .in('id', userIds);
      (profs || []).forEach((p: any) => { profileMap[p.id] = p; });
    }

    setAdmins(
      ((roles || []) as any[]).map((r): AdminRecord => {
        const p = profileMap[r.user_id] || {};
        return {
          roleId: r.id,
          userId: r.user_id,
          role: r.role,
          status: r.status || 'active',
          invitedBy: r.invited_by,
          invitedAt: r.invited_at,
          createdAt: r.created_at,
          full_name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.full_name || p.name || '—',
          first_name: p.first_name || null,
          last_name: p.last_name || null,
          display_name: p.display_name || null,
          email: p.email || '—',
          phone: p.phone || null,
          cpf: p.cpf || null,
          avatar_url: p.avatar_url || null,
          cep: p.cep || null,
          street: p.street || null,
          street_number: p.street_number || null,
          complement: p.complement || null,
          neighborhood: p.neighborhood || null,
          city: p.city || null,
          state: p.state || null,
          country: p.country || null,
          last_seen_at: p.last_seen_at || null,
        };
      })
    );
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—');
  const fmtRelative = (d: string | null) => {
    if (!d) return '—';
    const diff = Date.now() - new Date(d).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'Hoje';
    if (days === 1) return 'Ontem';
    if (days < 30) return `${days}d atrás`;
    return new Date(d).toLocaleDateString('pt-BR');
  };

  const filtered = admins.filter((a) => {
    const q = search.toLowerCase();
    return a.full_name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q);
  });

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[24px] font-700 text-white">Administradores</h1>
          <p className="text-white/40 text-[14px] mt-1">Equipe interna com acesso ao painel master</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-shield-user-add-line" />
          Adicionar administrador
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: admins.length, icon: 'ri-team-line', color: 'text-violet-400' },
          { label: 'Ativos', value: admins.filter((a) => a.status === 'active').length, icon: 'ri-checkbox-circle-line', color: 'text-emerald-400' },
          { label: 'Master', value: admins.filter((a) => a.role === 'master_admin').length, icon: 'ri-shield-star-line', color: 'text-violet-400' },
          { label: 'Inativos', value: admins.filter((a) => a.status !== 'active').length, icon: 'ri-pause-circle-line', color: 'text-orange-400' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center">
              <i className={`${stat.icon} text-[20px] ${stat.color}`} />
            </div>
            <div>
              <p className="text-[20px] font-700 text-white">{stat.value}</p>
              <p className="text-[11px] text-white/40">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1 max-w-sm">
          <i className="ri-search-line text-white/40 text-[16px]" />
          <input
            className="bg-transparent text-white text-[14px] outline-none placeholder:text-white/30 w-full"
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-white/30 hover:text-white/60 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <span className="text-[13px] text-white/40">{filtered.length} administrador{filtered.length !== 1 ? 'es' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-white/10">
                  {['Administrador', 'Papel', 'Status', 'Último acesso', 'Adicionado em', ''].map((h) => (
                    <th key={h} className="text-left px-5 py-3.5 text-[11px] font-600 text-white/40 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr
                    key={a.roleId}
                    onClick={() => navigate(`/admin/administradores/${a.userId}`)}
                    className="border-b border-white/5 hover:bg-violet-600/5 cursor-pointer transition-colors group"
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full shrink-0 overflow-hidden bg-violet-600/20 flex items-center justify-center">
                          {a.avatar_url ? (
                            <img src={a.avatar_url} alt={a.full_name} className="w-9 h-9 object-cover" />
                          ) : (
                            <span className="text-[14px] font-700 text-violet-300">{a.full_name[0]?.toUpperCase() || 'A'}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-[13px] font-500 text-white group-hover:text-violet-200 transition-colors">{a.full_name}</p>
                          <p className="text-[11px] text-white/40">{a.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`text-[11px] font-600 px-2.5 py-1 rounded-full ${roleColor[a.role] || 'bg-white/10 text-white/40'}`}>
                        {roleLabel[a.role] || a.role}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${a.status === 'active' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <span className={`text-[12px] ${a.status === 'active' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {a.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[12px] text-white/50">{fmtRelative(a.last_seen_at)}</td>
                    <td className="px-5 py-4 text-[12px] text-white/50">{fmt(a.createdAt)}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-white/30 group-hover:text-violet-400 transition-colors">
                        <span className="text-[11px]">Abrir ficha</span>
                        <i className="ri-arrow-right-s-line text-lg" />
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <i className="ri-shield-user-line text-3xl text-white/20" />
                        <p className="text-white/30 text-[13px]">
                          {search ? 'Nenhum administrador encontrado' : 'Nenhum administrador cadastrado'}
                        </p>
                        {!search && (
                          <button
                            onClick={() => setShowAdd(true)}
                            className="mt-1 px-4 py-2 bg-violet-600/20 text-violet-400 text-[12px] font-600 rounded-xl hover:bg-violet-600/30 cursor-pointer whitespace-nowrap transition-colors"
                          >
                            <i className="ri-add-line mr-1.5" />Adicionar primeiro administrador
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && (
        <AddAdminModal
          currentUserId={currentUserId}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
};

export default AdminAdministradoresPage;
